/**
 * Decides, per organization, whether the app identifies the user to Appcues (LSP-22199).
 *
 * Appcues bills by monthly active users and only counts users we identify(), so skipping identify() is
 * how we keep staging, E2E and test-account traffic out of the quota (Appcues cannot exclude a domain).
 *
 *   identify  =  (ENVIRONMENT is "production" AND org is not a test account)  OR  org is on the override list
 *
 * The override list is the runtime env var VITE_APPCUES_IDENTIFY_ORG_IDS (config/env.json → window.env):
 * comma-separated org IDs, edited in Heroku with no deploy. It lets chosen test orgs behave like real
 * customers, including on staging.
 */

export type AppcuesSkipReason = "test-account" | "not-on-override-list";

export type AppcuesIdentifyDecision = { identify: true } | { identify: false; reason: AppcuesSkipReason };

type AppcuesOrganization = { id?: string | null; isTestAccount?: boolean | null } | null | undefined;

function getOverrideOrgIds(): Set<string> {
	const configuredIds = window.env?.VITE_APPCUES_IDENTIFY_ORG_IDS ?? "";
	return new Set(
		configuredIds
			.split(",")
			.map((orgId) => orgId.trim().toLowerCase())
			.filter(Boolean)
	);
}

export function getAppcuesIdentifyDecision(organization: AppcuesOrganization): AppcuesIdentifyDecision {
	const organizationId = String(organization?.id ?? "")
		.trim()
		.toLowerCase();
	if (organizationId && getOverrideOrgIds().has(organizationId)) return { identify: true };

	// Exact match on purpose: any other value ("staging", "demo", a typo) counts as non-production.
	if (window.env?.ENVIRONMENT !== "production") return { identify: false, reason: "not-on-override-list" };
	// Only an explicit true is a test account; a missing flag is a normal customer.
	if (organization?.isTestAccount === true) return { identify: false, reason: "test-account" };
	return { identify: true };
}

/**
 * Forgets the user Appcues currently holds, for an org the policy skipped.
 *
 * Skipping identify() is not enough on its own: Appcues stores the identified user in the browser
 * (localStorage apc_user_id, sessionStorage apc_user) so it keeps tracking on later page loads without another
 * identify(). A browser identified earlier, or an in-page switch from an allowed org to a skipped one, would
 * therefore stay active and keep counting toward MAU.
 *
 * Two steps, because the loader is async and may not have run yet: reset() the SDK if it is there (documented
 * "clear all user data and in-progress Flows for the current session"), and always remove the two storage keys
 * Appcues documents so a script that loads later cannot restore the old user. Never throws; a failure is
 * reported with console.error so a lingering identity can be traced.
 */
export function clearAppcuesIdentity(): void {
	try {
		(window as any).Appcues?.reset?.();
	} catch (error) {
		console.error("Error clearing the AppCues identity (SDK reset failed)", error);
	}
	try {
		localStorage.removeItem("apc_user_id");
		sessionStorage.removeItem("apc_user");
	} catch (error) {
		console.error("Error clearing the AppCues identity (browser storage unavailable)", error);
	}
}
